import {
  AdaptivePointsMaterial,
  PotreeTree,
  WSKDTree,
  type FetcherClient,
  type LodPointCloud,
} from "@faro-lotv/lotv";
import { Box3, Vector3 } from "three";
import { loadLodNode } from "@/utils/load-lod-node";
import type { CylinderSurface } from "./cylinder-surface";
import { PipeFitNeighborhood } from "./pipe-fit-neighborhood";
import {
  tracePipeRun,
  type PipeEvidence,
  type PipeQuery,
  type PipeTraceResult,
} from "./pipe-tracker";

const LIMITS = {
  queryNodes: 128,
  queryPoints: 1_000_000,
  queryMilliseconds: 10_000,
  samples: 5000,
  cacheBytes: 32 * 1024 * 1024,
  estimatedPointBytes: 96,
  yieldPoints: 8192,
};

/**
 * @param cloud Source geometry and shared fetcher, never owned by the trace.
 * @param seed Accepted seed model in world space.
 * @param options Job lifetime, accepted updates and optional worker solver.
 * @returns Both frontier outcomes after releasing this job's fetcher leases.
 */
export function traceLodPipeRun(
  cloud: Pick<
    LodPointCloud,
    "lodTreeFetcher" | "tree" | "matrixWorld" | "material"
  >,
  seed: CylinderSurface,
  options: Parameters<typeof tracePipeRun>[2],
): Promise<PipeTraceResult> {
  return withLodPipeQuery(cloud, (query) => tracePipeRun(seed, query, options));
}

/**
 * @param cloud Source geometry and shared loader.
 * @param run Seed or trace operation using the same bounded, source-filtered query.
 * @returns The operation result after releasing owned caches and fetcher leases.
 */
export async function withLodPipeQuery<Result>(
  cloud: Parameters<typeof traceLodPipeRun>[0],
  run: (query: PipeQuery) => Promise<Result>,
): Promise<Result> {
  const client: FetcherClient = {
    lodTreeFetcher: cloud.lodTreeFetcher,
    tree: cloud.tree,
  };
  const fetcher = client.lodTreeFetcher;
  const matrix = cloud.matrixWorld.clone();
  const inverse = matrix.clone().invert();
  const scale = matrix.getMaxScaleOnAxis();
  const hidden = new Set(
    cloud.material instanceof AdaptivePointsMaterial
      ? cloud.material.hiddenScans
      : [],
  );
  const planes =
    cloud.material.clippingPlanes?.map((plane) => plane.clone()) ?? [];
  const { clipIntersection } = cloud.material;
  const cache = new Map<number, Vector3[]>();
  let cacheBytes = 0;
  fetcher.addClient(client);
  async function query(
    center: Vector3,
    radius: number,
    signal: AbortSignal,
    requestedSpacing = radius / 32,
  ): Promise<PipeEvidence> {
    const combined = AbortSignal.any([
      signal,
      AbortSignal.timeout(LIMITS.queryMilliseconds),
    ]);
    const box = new Box3(
      center.clone().addScalar(-radius),
      center.clone().addScalar(radius),
    ).applyMatrix4(inverse);
    let cellSize = requestedSpacing;
    let samples = new Map<string, { point: Vector3; distance: number }>();
    let sourceSpacing = Infinity;
    let visitedPoints = 0;
    let nodes = 0;
    let complete = true;
    function collect(point: Vector3): void {
      if (point.distanceToSquared(center) > radius * radius) return;
      const local = point.clone().sub(center).divideScalar(cellSize);
      const cell = local.clone().floor();
      const key = cell.toArray().join(",");
      const distance = local.distanceToSquared(cell.addScalar(0.5));
      const previous = samples.get(key);
      if (!previous || distance < previous.distance) {
        samples.set(key, { point, distance });
      }
      if (samples.size > LIMITS.samples) {
        const previousSamples = [...samples.values()];
        samples = new Map();
        cellSize *= 2;
        previousSamples.forEach(({ point }) => collect(point));
      }
    }
    const queue = [cloud.tree.root];
    function stopped(): boolean {
      return combined.aborted;
    }
    for (const node of queue) {
      if (!node.boundingBox.intersectsBox(box)) continue;
      if (stopped() || ++nodes > LIMITS.queryNodes) {
        complete = false;
        break;
      }
      let spacing = Infinity;
      if (cloud.tree instanceof PotreeTree) {
        spacing = cloud.tree.spacing / 2 ** node.depth;
      } else if (cloud.tree instanceof WSKDTree) {
        spacing = node.pointDensity / 1000;
      }
      spacing *= scale;
      if (spacing > 0) sourceSpacing = Math.min(sourceSpacing, spacing);
      if (node.numPoints > 0) {
        const existing = cache.get(node.id);
        if (existing) {
          cache.delete(node.id);
          cache.set(node.id, existing);
          for (const point of existing) {
            if (++visitedPoints > LIMITS.queryPoints || stopped()) {
              complete = false;
              break;
            }
            collect(point);
            if (visitedPoints % LIMITS.yieldPoints === 0) {
              await new Promise<void>((resolve) => setTimeout(resolve, 0));
            }
          }
        } else {
          try {
            const geometry = await loadLodNode(client, node, combined);
            const { position, scanIds } = geometry.attributes;
            const copied: Vector3[] | undefined =
              geometry.size * LIMITS.estimatedPointBytes <= LIMITS.cacheBytes
                ? []
                : undefined;
            for (let index = 0; index < geometry.size; index++) {
              if (++visitedPoints > LIMITS.queryPoints || stopped()) {
                complete = false;
                break;
              }
              if (hidden.has(scanIds?.getX(index) ?? 0)) continue;
              const point = new Vector3()
                .fromBufferAttribute(position, index)
                .applyMatrix4(matrix);
              if (!point.toArray().every(Number.isFinite)) continue;
              const clipped =
                planes.length > 0 &&
                (clipIntersection
                  ? planes.every((plane) => plane.distanceToPoint(point) < 0)
                  : planes.some((plane) => plane.distanceToPoint(point) < 0));
              if (!clipped) {
                copied?.push(point);
                collect(point);
              }
              if (index % LIMITS.yieldPoints === 0) {
                await new Promise<void>((resolve) => setTimeout(resolve, 0));
              }
            }
            if (copied && complete && !stopped()) {
              const bytes = copied.length * LIMITS.estimatedPointBytes;
              while (cacheBytes + bytes > LIMITS.cacheBytes && cache.size) {
                const oldest = cache.entries().next().value;
                if (!oldest) break;
                cache.delete(oldest[0]);
                cacheBytes -= oldest[1].length * LIMITS.estimatedPointBytes;
              }
              cache.set(node.id, copied);
              cacheBytes += bytes;
            }
          } catch {
            complete = false;
          } finally {
            fetcher.releaseNode(node.id, client);
          }
        }
      }
      if (!complete) break;
      if (!(node.numPoints > 0 && spacing <= requestedSpacing)) {
        queue.push(...node.children);
      }
    }
    const points = [...samples.values()].map(({ point }) => point);
    const spacing = Number.isFinite(sourceSpacing)
      ? sourceSpacing
      : (new PipeFitNeighborhood(points).spacing() ?? requestedSpacing);
    return {
      points,
      spacing,
      supportSpacing: Math.max(spacing, cellSize),
      complete: complete && !stopped(),
    };
  }
  try {
    return await run(query);
  } finally {
    cache.clear();
    fetcher.removeClient(client);
  }
}
